'use client';

import { useRouter } from 'next/navigation';
import { useState, type MouseEvent } from 'react';
import { UserRole } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { useLogout } from '@/hooks/useLogout';
import { MenuIcon } from '@/primitives/icons';
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@/primitives';
import { DocumentAvatar } from '@/components/avatars/DocumentAvatar';
import { userAvatarMessages } from './messages';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { PUBLIC_ROUTES } from '@/configs/routes';

export interface UserAvatarProps {
  /** When true, uses smaller avatar, text, and menu icon (e.g. for society header). */
  compact?: boolean;
}

const menuItemSx = {
  py: '6px',
  px: '12px',
  minHeight: 0,
  gap: '8px',
  fontSize: '13px',
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    color: 'text.secondary',
  },
} as const;

export function UserAvatar({ compact = false }: UserAvatarProps) {
  const router = useRouter();
  const { userProfile, userOrganisations, selectedOrganisationId } =
    usePrivate();
  const { logout } = useLogout();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const userOrganisationsFound = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const handleClickUserProfileButton = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClickAccount = () => {
    setAnchorEl(null);
    router.push('/account');
  };

  const handleClickRegistry = () => {
    setAnchorEl(null);
    router.push(PUBLIC_ROUTES.REGISTRY_LANDING);
  };

  const handleClickLogout = () => {
    setAnchorEl(null);
    logout();
  };

  const getInitials = () => {
    if (userProfile?.firstName) {
      return (
        (userProfile.firstName?.at(0) || '') +
        (userProfile.lastName?.at(0) || '')
      );
    }
    if (userProfile?.preferredName) {
      return userProfile.preferredName.at(0) || '';
    }
    return '';
  };

  const getName = () => {
    if (userProfile?.firstName) {
      return (
        userProfile.firstName +
        (userProfile.lastName ? ` ${userProfile.lastName}` : '')
      );
    }
    return userProfile?.preferredName || userProfile?.displayName;
  };

  return (
    <>
      <Box
        aria-controls={open ? 'user-profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        id="user-profile-button"
        onClick={handleClickUserProfileButton}
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <DocumentAvatar
          documentTypeCode={DocumentTypeCode.Avatar}
          documentEntityId={userProfile?.userProfileEntityId}
          sx={
            compact ? { width: 28, height: 28, fontSize: '0.75rem' } : undefined
          }
        >
          {getInitials()}
        </DocumentAvatar>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: compact ? '8px' : '12px',
          }}
        >
          <Typography
            variant={compact ? 'bodysmall' : 'titlemedium'}
            sx={{ color: 'text.primary' }}
          >
            {getName()}
          </Typography>
          {userOrganisationsFound && !compact && (
            <Typography variant="bodysmall" color="text.secondary">
              {UserRole[userOrganisationsFound.roleId!]}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <MenuIcon
          sx={{
            fontSize: compact ? '24px' : '48px',
            color: 'text.primary',
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="user-profile-menu"
        onClose={handleCloseMenu}
        autoFocus={false}
        open={open}
        slotProps={{
          list: {
            'aria-labelledby': 'user-profile-button',
            autoFocusItem: false,
            sx: { py: '4px' },
          },
          paper: {
            sx: { minWidth: 180, borderRadius: '8px', mt: '4px' },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem sx={menuItemSx} onClick={handleClickAccount}>
          <ListItemIcon>
            <PersonOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.account.label']}
        </MenuItem>
        <MenuItem sx={menuItemSx} onClick={handleClickRegistry}>
          <ListItemIcon>
            <AppRegistrationOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.registry.label']}
        </MenuItem>
        <Divider sx={{ my: '4px' }} />
        <MenuItem sx={menuItemSx} onClick={handleClickLogout}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.logout.label']}
        </MenuItem>
      </Menu>
    </>
  );
}
